import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      nav("/dashboard");
    } catch {
      setError("Email o contraseña incorrectos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Panel izquierdo - visual */}
      <div className="hidden md:flex flex-col justify-between w-[420px] bg-[#0D1B2A] p-10 flex-shrink-0">
        <div>
          <div className="flex items-center gap-2 mb-12">
            <div className="w-9 h-9 bg-blue-500 rounded-lg flex items-center justify-center">
              <ShieldCheck size={20} className="text-white"/>
            </div>
            <span className="text-white font-bold text-lg tracking-tight">Academia Elite</span>
          </div>
          <h2 className="text-white text-3xl font-bold leading-tight mb-4">
            Sistema integral de<br/>gestión deportiva
          </h2>
          <p className="text-white/50 text-sm leading-relaxed">
            Seguimiento de jugadores, wellness, estadísticas,
            torneos y pagos en un solo lugar.
          </p>
        </div>
        <div className="space-y-4">
          {[
            { icon: "📊", text: "Analytics e inteligencia artificial" },
            { icon: "💚", text: "Monitoreo de bienestar en tiempo real" },
            { icon: "🏆", text: "Gestión de torneos y tabla de posiciones" },
          ].map(item => (
            <div key={item.text} className="flex items-center gap-3">
              <span className="text-xl">{item.icon}</span>
              <span className="text-white/60 text-sm">{item.text}</span>
            </div>
          ))}
          <div className="pt-4 border-t border-white/10 text-white/30 text-xs">
            HPCS v1.0 · academia.solifycol.com
          </div>
        </div>
      </div>

      {/* Panel derecho - formulario */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <ShieldCheck size={16} className="text-white"/>
            </div>
            <span className="font-bold text-gray-900">Academia Elite</span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-1">Iniciar sesión</h1>
          <p className="text-gray-500 text-sm mb-8">Ingresa tus credenciales para continuar</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm flex items-center gap-2">
              <span className="flex-shrink-0">⚠</span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="admin@academia.com"
                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Contraseña</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white pr-10"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm shadow-blue-500/30"
            >
              {loading ? "Verificando..." : "Ingresar"}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-gray-400">
            ¿Problemas para acceder? Contacta al administrador del sistema.
          </p>
        </div>
      </div>
    </div>
  );
}
